/**
 * KbSearchPanel — RAGS/DPDS extraction panel for the KB Documents tab.
 *
 * Source KB is locked to the current KB. After launch, polls the transcript
 * endpoint until the extraction completes, then populates the results tab
 * directly (no WebSocket dependency).
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { extractionService, type ExtractionCostEstimate, type TranscriptDocumentHit } from '../services/extractionService';

type SearchType = 'rags' | 'dpds';
type Step = 'form' | 'cost' | 'launching' | 'polling';

const REGIME: Record<SearchType, 'A' | 'B'> = { rags: 'B', dpds: 'A' };

export interface KbSearchResultDocument {
  document_id: string;
  document_title?: string;
  relevance_score: number;
  justification?: string;
  relevance_explanation?: string;
}

interface Props {
  kbId: string;
  kbName: string;
  /** Called immediately after launch — creates the placeholder tab */
  onTabCreated: (transcriptId: string, label: string) => void;
  /** Called when polling completes — populates the tab with real documents */
  onResultsReady: (transcriptId: string, documents: KbSearchResultDocument[]) => void;
  /** Called when polling fails */
  onError: (transcriptId: string, message: string) => void;
}

const KbSearchPanel: React.FC<Props> = ({ kbId, kbName, onTabCreated, onResultsReady, onError }) => {
  const { t } = useTranslation();
  const [searchType, setSearchType] = useState<SearchType | ''>('');
  const [step, setStep] = useState<Step>('form');
  const [intent, setIntent] = useState('');
  const [keywords, setKeywords] = useState('');
  const [costEstimate, setCostEstimate] = useState<ExtractionCostEstimate | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setStep('form');
    setIntent('');
    setKeywords('');
    setCostEstimate(null);
    setError(null);
  };

  const handleTypeChange = (type: SearchType | '') => {
    setSearchType(type);
    resetForm();
  };

  const handleEstimate = async () => {
    if (!intent.trim() || (searchType !== 'rags' && searchType !== 'dpds')) return;
    setEstimating(true);
    setError(null);
    try {
      const est = await extractionService.estimateCostForKb(kbId, REGIME[searchType]);
      setCostEstimate(est);
      setStep('cost');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('extraction.error.estimate', 'Failed to estimate cost.'));
    } finally {
      setEstimating(false);
    }
  };

  const handleLaunch = async () => {
    if (!intent.trim() || (searchType !== 'rags' && searchType !== 'dpds')) return;
    setLaunching(true);
    setStep('launching');
    setError(null);
    try {
      const kwList = keywords.split(',').map(k => k.trim()).filter(Boolean);
      const res = await extractionService.launchForKb(kbId, {
        intent: intent.trim(),
        must_have_keywords: kwList,
        forced_regime: REGIME[searchType],
      });
      const label = `${searchType === 'rags' ? 'RAGS' : 'DPDS'} — ${intent.trim().substring(0, 30)}${intent.trim().length > 30 ? '…' : ''}`;

      // 1. Create placeholder tab immediately so the user sees it
      onTabCreated(res.transcript_id, label);

      // 2. Open transcript in new tab for live log view
      window.open(`/search-transcript/${res.transcript_id}`, '_blank');

      // 3. Poll until done, then populate the tab
      setStep('polling');
      extractionService.pollTranscriptUntilDone(
        res.transcript_id,
        (finalResult) => {
          const docs: KbSearchResultDocument[] = (finalResult.document_hits || []).map(
            (h: TranscriptDocumentHit) => ({
              document_id: h.doc_id,
              document_title: h.title,
              relevance_score: h.score / 100,
              justification: h.justification,
              relevance_explanation: h.relevance_explanation,
            })
          );
          onResultsReady(res.transcript_id, docs);
          resetForm();
          setSearchType('');
        },
        (errMsg) => {
          onError(res.transcript_id, errMsg);
          setError(errMsg);
          setStep('cost');
        },
      );
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('extraction.error.launch', 'Failed to launch search.'));
      setStep('cost');
    } finally {
      setLaunching(false);
    }
  };

  const formatCost = (cents: number, currency: string) =>
    `${currency === 'EUR' ? '€' : '$'}${(cents / 100).toFixed(2)}`;

  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 11, fontWeight: 600,
    color: '#374151', marginBottom: 3,
  };
  const inputStyle: React.CSSProperties = {
    width: '100%', fontSize: 12, padding: '4px 8px',
    border: '1px solid #d1d5db', borderRadius: 4,
    boxSizing: 'border-box',
  };
  const btnPrimary: React.CSSProperties = {
    fontSize: 11, padding: '3px 12px', borderRadius: 3,
    background: '#2563eb', color: '#fff', border: 'none',
    fontWeight: 600, cursor: 'pointer',
  };
  const btnSecondary: React.CSSProperties = {
    fontSize: 11, padding: '3px 12px', borderRadius: 3,
    background: '#f3f4f6', color: '#374151',
    border: '1px solid #d1d5db', cursor: 'pointer',
  };

  return (
    <div style={{
      padding: '8px 12px 8px',
      borderBottom: '1px solid #e5e7eb',
      background: '#fafafa',
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <label style={{ ...labelStyle, marginBottom: 0, whiteSpace: 'nowrap' }}>
          {t('kbSearch.searchType', 'AI Search')}
        </label>
        <select
          style={{ ...inputStyle, color: searchType ? '#111827' : '#9ca3af', flex: 1 }}
          value={searchType}
          onChange={e => handleTypeChange(e.target.value as SearchType | '')}
          disabled={step === 'polling'}
        >
          <option value="">{t('kbSearch.selectPlaceholder', 'Select method…')}</option>
          <option value="rags">{t('sota.search.rags.label', 'RAGS — Deep KB Retrieval')}</option>
          <option value="dpds">{t('sota.search.dpds.label', 'DPDS — Document-per-Document Search')}</option>
        </select>
      </div>

      {/* Polling indicator (search running in background) */}
      {step === 'polling' && (
        <p style={{ fontSize: 11, color: '#6b7280', fontStyle: 'italic', margin: 0 }}>
          {t('kbSearch.polling', 'Search running — results will appear in the tab when ready…')}
        </p>
      )}

      {/* RAGS / DPDS form */}
      {(searchType === 'rags' || searchType === 'dpds') && step !== 'polling' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {/* Regime banner */}
          <div style={{
            fontSize: 11, padding: '4px 8px', borderRadius: 4,
            background: searchType === 'rags' ? '#f5f3ff' : '#eff6ff',
            color: searchType === 'rags' ? '#6d28d9' : '#1d4ed8',
            border: `1px solid ${searchType === 'rags' ? '#c4b5fd' : '#93c5fd'}`,
          }}>
            {searchType === 'rags'
              ? t('extraction.regimeB.description', 'Documents ranked using combined keyword and semantic similarity search, then top results individually assessed.')
              : t('extraction.regimeA.description', 'Every document in this KB will be individually assessed by AI for relevance. Max 5000 documents.')}
          </div>

          {/* Source KB (locked) */}
          <div style={{ fontSize: 11, color: '#6b7280' }}>
            {t('kbSearch.sourceKb', 'Source KB')}: <strong>{kbName}</strong>
          </div>

          {step === 'form' && (
            <>
              <div>
                <label style={labelStyle}>
                  {t('extraction.intent', 'Intent')} <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <textarea
                  style={{ ...inputStyle, resize: 'vertical' }}
                  rows={2}
                  placeholder={t('extraction.intentPlaceholder', 'Describe what you are looking for in 1–3 sentences…')}
                  value={intent}
                  onChange={e => setIntent(e.target.value)}
                  maxLength={1000}
                />
              </div>
              <div>
                <label style={labelStyle}>
                  {t('extraction.keywords', 'Must-have keywords')}
                  <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 4 }}>
                    {t('extraction.optional', '(optional, comma-separated)')}
                  </span>
                </label>
                <input
                  type="text"
                  style={inputStyle}
                  placeholder={t('extraction.keywordsPlaceholder', 'e.g. thermal, heat exchanger')}
                  value={keywords}
                  onChange={e => setKeywords(e.target.value)}
                />
              </div>
              {error && <p style={{ fontSize: 11, color: '#dc2626', margin: 0 }}>{error}</p>}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  style={{ ...btnPrimary, opacity: (!intent.trim() || estimating) ? 0.5 : 1 }}
                  disabled={!intent.trim() || estimating}
                  onClick={handleEstimate}
                >
                  {estimating ? t('extraction.estimating', 'Estimating…') : t('extraction.nextEstimate', 'Estimate Cost')}
                </button>
              </div>
            </>
          )}

          {step === 'cost' && costEstimate && (
            <>
              <div style={{
                background: '#f9fafb', border: '1px solid #e5e7eb',
                borderRadius: 4, padding: '8px 10px', fontSize: 11,
                display: 'flex', flexDirection: 'column', gap: 3,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{t('extraction.costDocs', 'Documents in KB')}</span>
                  <span>{costEstimate.total_documents.toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{t('extraction.costLlmCalls', 'Estimated LLM calls')}</span>
                  <span>{costEstimate.estimated_llm_calls.toLocaleString()}</span>
                </div>
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  fontWeight: 700, borderTop: '1px solid #e5e7eb', paddingTop: 4, marginTop: 2,
                }}>
                  <span>{t('extraction.costEstimated', 'Estimated cost')}</span>
                  <span>{formatCost(costEstimate.estimated_cost_cents, costEstimate.currency)}</span>
                </div>
              </div>
              {error && <p style={{ fontSize: 11, color: '#dc2626', margin: 0 }}>{error}</p>}
              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                <button style={btnSecondary} onClick={() => setStep('form')}>{t('common.back', 'Back')}</button>
                <button style={btnPrimary} disabled={launching} onClick={handleLaunch}>
                  {launching ? t('extraction.launching', 'Launching…') : t('extraction.confirmLaunch', 'Confirm & Launch')}
                </button>
              </div>
            </>
          )}

          {step === 'launching' && (
            <p style={{ fontSize: 11, color: '#6b7280', fontStyle: 'italic', margin: 0 }}>
              {t('kbSearch.launching', 'Launching…')}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default KbSearchPanel;
